import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as ai from "../../services/aiAssistantService";
import { languageLabel } from "./aiHelpers";

const REVIEW_BADGE = { good: "text-bg-success", bad: "text-bg-danger" };

// Recent conversations the assistant took part in, with the words and the lookups it made, so a person can
// judge them. A "good" or "bad" mark is the quickest way to tell UtilStop what to fix.
const ConversationsTab = () => {
  const [rows, setRows] = useState(null);
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState(null); // transcript being read
  const [loadingOpen, setLoadingOpen] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);

  const load = useCallback(async (before) => {
    try {
      const list = await ai.getTranscripts({ before });
      setRows((prev) => (before && prev ? [...prev, ...list] : list));
      setMore(list.length >= 30);
      setError(null);
    } catch (e) {
      setError((e && e.message) || "Could not load conversations");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const read = async (id) => {
    setLoadingOpen(true);
    setNote("");
    try {
      setOpen(await ai.getTranscript(id));
    } catch (e) {
      toast.error((e && e.message) || "Could not open the conversation");
    } finally {
      setLoadingOpen(false);
    }
  };

  const review = async (tag) => {
    const res = await ai.reviewTranscript(open.id, tag, note);
    if (res && res.success) {
      toast.success(tag === "good" ? "Marked as good" : "Marked as needing a fix");
      setOpen({ ...open, review: { tag, note } });
      setRows((prev) => (prev || []).map((r) => (r.id === open.id ? { ...r, review: tag } : r)));
    } else {
      toast.error((res && res.message) || "Could not save the review");
    }
  };

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!rows) return <div className="text-muted py-4">Loading…</div>;
  if (rows.length === 0) {
    return <div className="text-center text-muted py-5 small">No conversations yet. They appear here once the assistant has handled a chat.</div>;
  }

  return (
    <>
      <div className="table-responsive card shadow-sm rounded-4">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr className="small text-muted">
              <th>Customer</th>
              <th>Language</th>
              <th>Messages</th>
              <th>Last message</th>
              <th>Owner</th>
              <th>Review</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => read(r.id)}>
                <td>{r.customer || r.phone}</td>
                <td>{languageLabel(r.language) || "-"}</td>
                <td>{r.turns}</td>
                <td className="text-nowrap">{r.last_message_at}</td>
                <td>{r.state === "human" ? "Team" : "Assistant"}</td>
                <td>{r.review ? <span className={`badge ${REVIEW_BADGE[r.review]}`}>{r.review === "good" ? "Good" : "Needs a fix"}</span> : <span className="text-muted small">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {more && (
        <div className="text-center mt-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => load(rows[rows.length - 1].id)}>
            Show older
          </button>
        </div>
      )}
      {loadingOpen && <div className="text-muted small mt-2">Opening…</div>}

      {open && (
        <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Conversation {languageLabel(open.language) ? `· ${languageLabel(open.language)}` : ""}</h6>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setOpen(null)} />
              </div>
              <div className="modal-body" style={{ background: "#f6f7f9" }}>
                {open.summary && <div className="alert alert-light border small">Summary: {open.summary}</div>}
                {open.messages.map((m, i) => (
                  <div key={i} className={`d-flex mb-2 ${m.role === "user" ? "" : "justify-content-end"}`}>
                    <div
                      className="rounded-3 px-3 py-2 small"
                      style={{ maxWidth: "80%", background: m.role === "user" ? "#fff" : "#e3f2fd", boxShadow: "0 1px 2px rgba(0,0,0,0.08)", whiteSpace: "pre-wrap" }}
                    >
                      {m.text}
                      {m.tools.length > 0 && <div className="text-muted fst-italic">looked up: {m.tools.join(", ")}</div>}
                      <div className="text-muted" style={{ fontSize: 10 }}>
                        {m.role === "user" ? "Customer" : "Assistant"} · {m.at}
                      </div>
                    </div>
                  </div>
                ))}
                {open.tool_calls.length > 0 && (
                  <details className="mt-3 small">
                    <summary>What the assistant looked up ({open.tool_calls.length})</summary>
                    {open.tool_calls.map((t, i) => (
                      <div className="border rounded-2 bg-white p-2 mt-2" key={i}>
                        <strong>{t.tool}</strong> <span className={`badge ${t.status === "ok" ? "text-bg-success" : "text-bg-warning"}`}>{t.status}</span>
                        <pre className="mb-1 mt-1 small text-muted" style={{ whiteSpace: "pre-wrap" }}>{JSON.stringify(t.input)}</pre>
                        <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap", maxHeight: 140, overflow: "auto" }}>{t.output}</pre>
                      </div>
                    ))}
                  </details>
                )}
              </div>
              <div className="modal-footer d-flex gap-2">
                <input className="form-control form-control-sm" style={{ flex: 1 }} placeholder="What was right or wrong? (optional)" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
                {open.review && <span className={`badge ${REVIEW_BADGE[open.review.tag]}`}>{open.review.tag === "good" ? "Marked good" : "Marked needs a fix"}</span>}
                <button type="button" className="btn btn-sm btn-outline-success" onClick={() => review("good")}>
                  Good
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => review("bad")}>
                  Needs a fix
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConversationsTab;
